import { Button, Select, SelectItem, Switch } from '@heroui/react';
import { AlignCenter, AlignLeft, AlignRight, BookmarkPlus } from 'lucide-react';
import React, { FC } from 'react';

import { useHeroToast } from '../../../../hooks';
import { useAdGeneratorSettings, useUpdateAdGeneratorSettings } from '../../../../queries';
import { findFont, FONT_OPTIONS } from '../../config/fonts';
import { useSceneEditing } from '../../hooks';
import { useAdsGeneratorStore } from '../../model/store';
import { TextAlign, TextElement } from '../../model/types';
import { ColorField, PanelSection, SliderField } from '../common';

interface TextPropertiesProps {
  element: TextElement;
}

const ALIGNMENTS: { value: TextAlign; icon: typeof AlignLeft; label: string }[] = [
  { value: 'left', icon: AlignLeft, label: 'Do lewej' },
  { value: 'center', icon: AlignCenter, label: 'Wyśrodkuj' },
  { value: 'right', icon: AlignRight, label: 'Do prawej' },
];

export const TextProperties: FC<TextPropertiesProps> = ({ element }) => {
  const { patchElement } = useSceneEditing();
  const scene = useAdsGeneratorStore((state) => state.scene);
  const settings = useAdGeneratorSettings();
  const { mutateAsync: saveSettings, isPending: isSavingStyle } = useUpdateAdGeneratorSettings();
  const { showError, showSuccess } = useHeroToast();
  const font = findFont(element.fontFamily);

  /**
   * Zapisuje bieżący wygląd napisu jako styl. Stopień pisma przeliczamy na
   * ułamek krótszego boku planszy, żeby ten sam styl działał w każdym formacie.
   */
  const saveAsStyle = async (): Promise<void> => {
    if (!scene) return;
    const shortEdge = Math.min(scene.width, scene.height) || 1;

    try {
      await saveSettings({
        textStyles: [
          ...settings.textStyles,
          {
            id: `style-${Date.now()}`,
            name: element.text.trim().slice(0, 24) || element.name,
            fontFamily: element.fontFamily,
            sizeRatio: Math.round((element.fontSize / shortEdge) * 10000) / 10000,
            fontWeight: element.fontWeight,
            color: element.color,
            uppercase: element.uppercase,
            letterSpacing: element.letterSpacing,
            lineHeight: element.lineHeight,
          },
        ],
      });
      showSuccess('Styl zapisany, znajdziesz go w panelu Tekst');
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Nie udało się zapisać stylu');
    }
  };

  return (
    <>
      <PanelSection
        title="Typografia"
        action={(
          <Button
            size="sm"
            radius="sm"
            isIconOnly
            variant="light"
            aria-label="Zapisz jako styl"
            title="Zapisz jako styl"
            isLoading={isSavingStyle}
            onPress={saveAsStyle}
          >
            <BookmarkPlus size={14} />
          </Button>
        )}
      >
        <Select
          size="sm"
          radius="sm"
          label="Krój"
          labelPlacement="outside"
          aria-label="Krój pisma"
          selectedKeys={new Set([element.fontFamily])}
          onSelectionChange={(keys) => {
            const [next] = Array.from(keys as Set<string>);
            if (!next) return;
            const target = findFont(next);
            patchElement(element.id, {
              fontFamily: next,
              fontWeight: target.weights.includes(element.fontWeight) ? element.fontWeight : target.weights[0],
              fontStyle: target.italic ? element.fontStyle : 'normal',
            });
          }}
        >
          {FONT_OPTIONS.map((option) => (
            <SelectItem key={option.family} textValue={option.label}>
              {option.label}
            </SelectItem>
          ))}
        </Select>

        <Select
          size="sm"
          radius="sm"
          label="Grubość"
          labelPlacement="outside"
          aria-label="Grubość pisma"
          selectedKeys={new Set([String(element.fontWeight)])}
          onSelectionChange={(keys) => {
            const [next] = Array.from(keys as Set<string>);
            if (next) patchElement(element.id, { fontWeight: Number(next) });
          }}
        >
          {font.weights.map((weight) => (
            <SelectItem key={String(weight)} textValue={String(weight)}>
              {String(weight)}
            </SelectItem>
          ))}
        </Select>

        <SliderField
          label="Rozmiar"
          suffix=" px"
          min={8}
          max={320}
          value={element.fontSize}
          onChange={(fontSize) => patchElement(element.id, { fontSize }, `font-size-${element.id}`)}
        />
        <SliderField
          label="Interlinia"
          min={0.8}
          max={2.4}
          step={0.05}
          value={element.lineHeight}
          onChange={(lineHeight) => patchElement(element.id, { lineHeight }, `line-height-${element.id}`)}
        />
        <SliderField
          label="Odstęp liter"
          suffix=" px"
          min={-10}
          max={40}
          value={element.letterSpacing}
          onChange={(letterSpacing) => patchElement(element.id, { letterSpacing }, `tracking-${element.id}`)}
        />
      </PanelSection>

      <PanelSection title="Układ">
        <div className="flex gap-1">
          {ALIGNMENTS.map(({ value, icon: Icon, label }) => (
            <Button
              key={value}
              size="sm"
              radius="sm"
              isIconOnly
              aria-label={label}
              variant={element.align === value ? 'solid' : 'flat'}
              color={element.align === value ? 'primary' : 'default'}
              onPress={() => patchElement(element.id, { align: value })}
            >
              <Icon size={16} />
            </Button>
          ))}
        </div>
        <Switch
          size="sm"
          isSelected={element.uppercase}
          onValueChange={(uppercase) => patchElement(element.id, { uppercase })}
        >
          <span className="text-xs">Wielkie litery</span>
        </Switch>
        <Switch
          size="sm"
          isSelected={element.autoFit}
          onValueChange={(autoFit) => patchElement(element.id, { autoFit })}
        >
          <span className="text-xs">Dopasuj tekst do ramki</span>
        </Switch>
        {font.italic && (
          <Switch
            size="sm"
            isSelected={element.fontStyle === 'italic'}
            onValueChange={(italic) => patchElement(element.id, { fontStyle: italic ? 'italic' : 'normal' })}
          >
            <span className="text-xs">Kursywa</span>
          </Switch>
        )}
      </PanelSection>

      <PanelSection title="Kolory">
        <ColorField
          label="Obrys liter"
          value={element.stroke}
          allowEmpty
          onChange={(stroke) => patchElement(element.id, { stroke }, `text-stroke-${element.id}`)}
        />
        {Boolean(element.stroke) && (
          <SliderField
            label="Grubość obrysu"
            suffix=" px"
            min={1}
            max={30}
            value={element.strokeWidth || 1}
            onChange={(strokeWidth) => patchElement(element.id, { strokeWidth }, `text-stroke-width-${element.id}`)}
          />
        )}
        <ColorField
          label="Kolor tekstu"
          value={element.color}
          onChange={(color) => patchElement(element.id, { color }, `text-color-${element.id}`)}
        />
        <ColorField
          label="Tło plakietki"
          value={element.background}
          allowEmpty
          onChange={(background) => patchElement(element.id, { background }, `text-bg-${element.id}`)}
        />
        {Boolean(element.background) && (
          <>
            <SliderField
              label="Zaokrąglenie tła"
              suffix=" px"
              min={0}
              max={999}
              value={element.backgroundRadius}
              onChange={(backgroundRadius) => patchElement(element.id, { backgroundRadius }, `text-radius-${element.id}`)}
            />
            <SliderField
              label="Odstęp wewnętrzny"
              suffix=" px"
              min={0}
              max={160}
              value={element.padding}
              onChange={(padding) => patchElement(element.id, { padding }, `text-padding-${element.id}`)}
            />
          </>
        )}
      </PanelSection>
    </>
  );
};
